// src/components/padrekko/qc/PhotoLightbox.jsx
//
// Full-size photo modal with an SVG annotation overlay (arrows / circles /
// text). Pure SVG + React — the geometry is simple (line+arrowhead, circle,
// text) and SVG renders crisply at any scale and statically in view mode, so a
// canvas lib (Konva/Fabric) would only add bytes.
//
// Annotations are a SEPARATE overlay: stored as normalized 0..1 coords in the
// attachment's `annotations` column, never burned into the S3 original. Coords
// are normalized so the overlay tracks the photo at any display size; circle
// radius is a fraction of image WIDTH so circles stay true circles (not
// ellipses) regardless of aspect.
//
// Polish: ~80vw×80vh modal, photo letterboxed to fit, caption below (only when
// present), uploader/date metadata in the header, prev/next nav + counter for
// multi-photo sets, Esc + click-outside to close.

import { useEffect, useRef, useState, useCallback } from "react";
import {
  X as XIcon, Download, ChevronLeft, ChevronRight, Pencil, Check, Loader2, Lock,
  ArrowUpRight, Circle as CircleIcon, Type, MousePointer2, Trash2,
} from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import { useAuthStore } from "../../../stores/useAuthStore";

const RED = "#ef4444";
const STROKE = 3;
const MIN_ARROW = 0.01; // normalized min drag to count as an arrow
const MIN_RADIUS = 0.01;
const clamp01 = (n) => Math.min(Math.max(n, 0), 1);

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
function uid() {
  try {
    return crypto.randomUUID();
  } catch {
    return `a-${Math.round(performance.now())}-${Math.floor((performance.now() * 1000) % 100000)}`;
  }
}

export default function PhotoLightbox({ photos = [], index = 0, onClose }) {
  const list = Array.isArray(photos) ? photos.filter(Boolean) : [];
  const [idx, setIdx] = useState(Math.min(Math.max(index, 0), Math.max(list.length - 1, 0)));
  const photo = list[idx];

  // can_annotate is computed server-side (same check that authorizes the save),
  // so the toolbar is never offered when a save would 403. userId is kept for
  // stamping created_by on new annotations.
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const saveAnnotationsAction = useQcStore((s) => s.saveAnnotations);
  const saveInflightId = useQcStore((s) => s.annotationSaveInflightId);
  const canAnnotate = photo?.can_annotate === true;

  // Annotation working state.
  const [mode, setMode] = useState("view"); // 'view' | 'annotate'
  const [tool, setTool] = useState("arrow"); // arrow|circle|text|select
  const [annots, setAnnots] = useState(photo?.annotations ?? []);
  const [draft, setDraft] = useState(null); // in-progress shape preview
  const [selectedId, setSelectedId] = useState(null);
  const [textDraft, setTextDraft] = useState(null); // {x,y,value}
  const [error, setError] = useState(null);
  const [prevPhotoId, setPrevPhotoId] = useState(photo?.id);

  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const textInputRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [aspect, setAspect] = useState(null); // natW/natH — sizes the wrapper
  const [drag, setDrag] = useState(null); // { id, start:{x,y}, orig:[{x,y}] }
  const saving = saveInflightId === photo?.id;

  // Focus the text-callout input once it mounts (post-render, after the
  // placement click settles). Guard against re-focusing mid-typing (which would
  // jump the caret). Not setState — safe in an effect under the strict config.
  useEffect(() => {
    const el = textInputRef.current;
    if (textDraft && el && document.activeElement !== el) el.focus();
  }, [textDraft]);

  // Re-init annotations + reset transient state when the photo changes. Done as
  // a render-time prop-change adjustment (the codebase's pattern) rather than a
  // setState-in-effect.
  if (prevPhotoId !== photo?.id) {
    setPrevPhotoId(photo?.id);
    setAnnots(photo?.annotations ?? []);
    setMode("view");
    setDraft(null);
    setSelectedId(null);
    setTextDraft(null);
    setError(null);
  }

  // Track the rendered image box so normalized coords map to pixels.
  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (el) setBox({ w: el.clientWidth, h: el.clientHeight });
  }, []);
  function onImgLoad(e) {
    const nw = e.target?.naturalWidth, nh = e.target?.naturalHeight;
    if (nw && nh) setAspect(nw / nh);
    measure();
  }
  useEffect(() => {
    measure();
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, photo?.id]);

  const goPrev = useCallback(() => { setIdx((i) => (i > 0 ? i - 1 : i)); }, []);
  const goNext = useCallback(() => { setIdx((i) => (i < list.length - 1 ? i + 1 : i)); }, [list.length]);

  const exitAnnotate = useCallback(() => {
    setMode("view");
    setDraft(null);
    setSelectedId(null);
    setTextDraft(null);
    setAnnots(photo?.annotations ?? []); // discard unsaved
    setError(null);
  }, [photo?.annotations]);

  // Keyboard: Esc closes (view) or cancels (annotate); arrows navigate in view.
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        if (textDraft) { setTextDraft(null); return; }
        if (mode === "annotate") { exitAnnotate(); return; }
        onClose();
      } else if (mode === "view" && list.length > 1) {
        if (e.key === "ArrowLeft") goPrev();
        else if (e.key === "ArrowRight") goNext();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, textDraft, list.length, exitAnnotate, onClose, goPrev, goNext]);

  if (!photo) return null;

  const src = photo.url || photo.thumb_md_url;

  // Pointer → normalized [0,1] within the image box.
  function toNorm(e) {
    const el = wrapRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    const x = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1);
    const y = Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1);
    return { x, y };
  }

  function commitAnnot(a) {
    setAnnots((prev) => [
      ...prev,
      { id: uid(), color: "red", created_by: userId, created_at: new Date().toISOString(), ...a },
    ]);
  }

  function onPointerDown(e) {
    if (mode !== "annotate") return;
    if (tool === "select") { setSelectedId(null); return; }
    const p = toNorm(e);
    if (tool === "text") {
      // preventDefault stops the SVG from grabbing focus on mouseup, which
      // would otherwise blur (and commit-empty) the text input we're about to
      // mount. A post-render effect then focuses it reliably.
      e.preventDefault();
      setTextDraft({ x: p.x, y: p.y, value: "" });
      return;
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDraft({ tool, start: p, end: p });
  }

  // Grab a shape in select mode → select it and begin a move. The whole
  // annotation translates rigidly (all coords shift by the same delta), so the
  // pointer "holds" wherever it grabbed.
  function onShapePointerDown(a, e) {
    if (!interactive || tool !== "select") return;
    e.stopPropagation();
    setSelectedId(a.id);
    setDrag({ id: a.id, start: toNorm(e), orig: a.coords.map((c) => ({ x: c.x, y: c.y })) });
    svgRef.current?.setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e) {
    if (drag) {
      const p = toNorm(e);
      let dx = p.x - drag.start.x;
      let dy = p.y - drag.start.y;
      // Clamp the delta (not each coord) so the shape stays rigid and on-image.
      for (const c of drag.orig) {
        dx = Math.min(dx, 1 - c.x); dx = Math.max(dx, -c.x);
        dy = Math.min(dy, 1 - c.y); dy = Math.max(dy, -c.y);
      }
      setAnnots((prev) =>
        prev.map((a) =>
          a.id === drag.id
            ? { ...a, coords: drag.orig.map((c) => ({ x: clamp01(c.x + dx), y: clamp01(c.y + dy) })) }
            : a
        )
      );
      return;
    }
    if (!draft) return;
    setDraft((d) => (d ? { ...d, end: toNorm(e) } : d));
  }
  function onPointerUp(e) {
    if (drag) {
      svgRef.current?.releasePointerCapture?.(e.pointerId);
      setDrag(null);
      return;
    }
    if (!draft) return;
    const { tool: t, start, end } = draft;
    if (t === "arrow") {
      const dist = Math.hypot(end.x - start.x, end.y - start.y);
      if (dist >= MIN_ARROW) commitAnnot({ type: "arrow", coords: [start, end] });
    } else if (t === "circle") {
      const dx = (end.x - start.x) * box.w;
      const dy = (end.y - start.y) * box.h;
      const radius = box.w ? Math.hypot(dx, dy) / box.w : 0; // fraction of width
      if (radius >= MIN_RADIUS) commitAnnot({ type: "circle", coords: [start], radius });
    }
    setDraft(null);
  }

  function commitText() {
    const t = textDraft;
    if (t && t.value.trim()) {
      commitAnnot({ type: "text", coords: [{ x: t.x, y: t.y }], text: t.value.trim() });
    }
    setTextDraft(null);
  }

  function removeSelected() {
    if (!selectedId) return;
    setAnnots((prev) => prev.filter((a) => a.id !== selectedId));
    setSelectedId(null);
  }

  async function handleSave() {
    setError(null);
    const result = await saveAnnotationsAction(photo.id, annots);
    if (result.ok) setMode("view");
    else setError(result.error || "Failed to save annotations.");
  }

  // ── SVG rendering ─────────────────────────────────────────────────────────
  const px = (n, dim) => n * (dim === "x" ? box.w : box.h);
  const interactive = mode === "annotate";

  function renderShape(a) {
    const selecting = interactive && tool === "select";
    const selected = selectedId === a.id && selecting;
    const sw = selected ? STROKE + 2 : STROKE;
    // In select mode the shape is grab-to-move; a wide transparent hit shape
    // makes thin arrows/circles easy to grab. Pointer-down selects + drags.
    const pd = selecting ? (e) => onShapePointerDown(a, e) : undefined;
    const cursor = selecting ? (drag?.id === a.id ? "grabbing" : "move") : "default";
    const visPE = "none"; // the visible shape never intercepts; the wide hit shape does
    if (a.type === "arrow") {
      const [s, t] = a.coords;
      const x1 = px(s.x, "x"), y1 = px(s.y, "y"), x2 = px(t.x, "x"), y2 = px(t.y, "y");
      return (
        <g key={a.id} onPointerDown={pd} style={{ cursor }}>
          {selecting && (
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={18}
              strokeLinecap="round" style={{ pointerEvents: "stroke" }} />
          )}
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={RED} strokeWidth={sw}
            strokeLinecap="round" markerEnd="url(#pl-arrow)" style={{ pointerEvents: visPE }} />
        </g>
      );
    }
    if (a.type === "circle") {
      const c = a.coords[0];
      const cx = px(c.x, "x"), cy = px(c.y, "y"), r = (a.radius || 0) * box.w;
      return (
        <g key={a.id} onPointerDown={pd} style={{ cursor }}>
          {selecting && (
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="transparent" strokeWidth={18}
              style={{ pointerEvents: "stroke" }} />
          )}
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={RED} strokeWidth={sw}
            style={{ pointerEvents: visPE }} />
        </g>
      );
    }
    if (a.type === "text") {
      const c = a.coords[0];
      return (
        <text key={a.id} x={px(c.x, "x")} y={px(c.y, "y")} fill="#fff" stroke="#000" strokeWidth="3"
          paintOrder="stroke" fontSize="15" fontWeight="700" dominantBaseline="middle"
          onPointerDown={pd} style={{ pointerEvents: selecting ? "all" : "none", cursor }}>
          {a.text}
        </text>
      );
    }
    return null;
  }

  function renderDraft() {
    if (!draft) return null;
    const { tool: t, start, end } = draft;
    if (t === "arrow") {
      return <line x1={px(start.x, "x")} y1={px(start.y, "y")} x2={px(end.x, "x")} y2={px(end.y, "y")}
        stroke={RED} strokeWidth={STROKE} strokeLinecap="round" markerEnd="url(#pl-arrow)" opacity="0.85" />;
    }
    if (t === "circle") {
      const dx = (end.x - start.x) * box.w, dy = (end.y - start.y) * box.h;
      return <circle cx={px(start.x, "x")} cy={px(start.y, "y")} r={Math.hypot(dx, dy)}
        fill="none" stroke={RED} strokeWidth={STROKE} opacity="0.85" />;
    }
    return null;
  }

  const counter = list.length > 1 ? `Photo ${idx + 1} of ${list.length}` : null;
  const toolBtns = [
    { id: "arrow", Icon: ArrowUpRight, label: "Arrow" },
    { id: "circle", Icon: CircleIcon, label: "Circle" },
    { id: "text", Icon: Type, label: "Text" },
    { id: "select", Icon: MousePointer2, label: "Select" },
  ];

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-[92vw] h-[90vh] flex flex-col bg-bg-soft border border-slate-700 rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — metadata + counter + actions */}
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2 min-w-0 text-[10px] text-fg-secondary">
            {counter && <span className="text-slate-400 tabular-nums whitespace-nowrap">{counter}</span>}
            {counter && <span className="text-slate-700">·</span>}
            <span className="truncate">
              Uploaded by {photo.uploader_name || "Unknown"}
              {photo.uploaded_at ? ` · ${fmtDate(photo.uploaded_at)}` : ""}
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {mode === "view" && canAnnotate && (
              <button type="button" onClick={() => setMode("annotate")}
                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border
                           bg-slate-800/60 border-slate-700 text-slate-200 hover:bg-slate-800 transition"
                title="Annotate this photo">
                <Pencil size={11} /> Annotate
              </button>
            )}
            {mode === "view" && !canAnnotate && (
              <span className="flex items-center gap-1 text-[10px] px-2 py-1 text-fg-secondary"
                title="You don't have permission to annotate this photo at this stage">
                <Lock size={10} /> Read-only
              </span>
            )}
            {src && (
              <a href={src} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/10 transition"
                title="Open full image in a new tab">
                <Download size={15} />
              </a>
            )}
            <button type="button" onClick={onClose}
              className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/10 transition" title="Close">
              <XIcon size={17} />
            </button>
          </div>
        </div>

        {/* Annotation toolbar — top, only in annotate mode. Wraps (left:
            tools, right: actions) so Save never clips on a narrow modal. */}
        {mode === "annotate" && (
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5
                          px-4 py-1.5 border-b border-slate-800 bg-slate-900/50 shrink-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              {toolBtns.map((tb) => (
                <button key={tb.id} type="button" onClick={() => { setTool(tb.id); setSelectedId(null); }}
                  className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded border transition
                    ${tool === tb.id
                      ? "bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]"
                      : "bg-slate-800/50 border-slate-700/60 text-slate-300 hover:bg-slate-800"}`}
                  title={tb.label}>
                  <tb.Icon size={11} /> {tb.label}
                </button>
              ))}
              {tool === "select" && (
                <button type="button" onClick={removeSelected} disabled={!selectedId}
                  className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                             bg-red-500/10 border-red-500/40 text-red-300 hover:bg-red-500/20
                             disabled:opacity-40 disabled:cursor-not-allowed transition"
                  title="Delete selected annotation">
                  <Trash2 size={11} /> Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] text-fg-secondary mr-1 whitespace-nowrap">
                {annots.length} annotation{annots.length === 1 ? "" : "s"}
              </span>
              <button type="button" onClick={exitAnnotate} disabled={saving}
                className="text-[10px] px-2 py-1 rounded border border-slate-700 text-slate-300
                           hover:bg-slate-800 disabled:opacity-40 transition">
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={saving}
                className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded border
                           bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4] hover:bg-[#16f2d4]/25
                           disabled:opacity-40 disabled:cursor-not-allowed transition">
                {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}

        {/* Canvas — photo + overlay, letterboxed */}
        <div className="relative flex-1 min-h-0 flex items-center justify-center bg-black/40 p-2">
          {mode === "view" && list.length > 1 && (
            <>
              <button type="button" onClick={goPrev} disabled={idx === 0}
                className="absolute left-2 z-10 p-1.5 rounded-full bg-black/50 text-slate-200
                           hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed transition"
                title="Previous photo">
                <ChevronLeft size={20} />
              </button>
              <button type="button" onClick={goNext} disabled={idx === list.length - 1}
                className="absolute right-2 z-10 p-1.5 rounded-full bg-black/50 text-slate-200
                           hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed transition"
                title="Next photo">
                <ChevronRight size={20} />
              </button>
            </>
          )}

          {/* Wrapper takes the image's aspect and fits within the canvas, so
              its box equals the displayed picture exactly — the SVG overlay
              (inset-0) then aligns to the photo with no letterbox offset. */}
          <div
            ref={wrapRef}
            className="relative max-w-full max-h-full leading-none"
            style={{ aspectRatio: aspect || undefined }}
          >
            <img
              src={src}
              alt={photo.caption || photo.filename}
              onLoad={onImgLoad}
              className="block w-full h-full object-contain select-none"
              draggable={false}
            />
            <svg
              ref={svgRef}
              className="absolute inset-0"
              width={box.w || "100%"}
              height={box.h || "100%"}
              style={{
                touchAction: "none",
                cursor: !interactive || tool === "select"
                  ? "default"
                  : tool === "text" ? "text" : "crosshair",
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              <defs>
                <marker id="pl-arrow" markerWidth="10" markerHeight="10" refX="7" refY="3.5"
                  orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L8,3.5 L0,7 Z" fill={RED} />
                </marker>
              </defs>
              {annots.map(renderShape)}
              {renderDraft()}
            </svg>

            {/* Inline text-entry box while placing a text callout */}
            {textDraft && box.w > 0 && (
              <input
                ref={textInputRef}
                value={textDraft.value}
                onChange={(e) => setTextDraft((t) => ({ ...t, value: e.target.value }))}
                onBlur={commitText}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); commitText(); }
                  else if (e.key === "Escape") { e.preventDefault(); setTextDraft(null); }
                }}
                placeholder="Label, then Enter…"
                className="absolute z-10 text-[12px] px-1 py-0.5 rounded bg-slate-900/95 border border-[#16f2d4]/60
                           text-white outline-none shadow-lg"
                style={{
                  left: Math.min(textDraft.x * box.w, Math.max(box.w - 150, 0)),
                  top: Math.min(textDraft.y * box.h, Math.max(box.h - 26, 0)),
                  width: 150,
                }}
              />
            )}
          </div>
        </div>

        {error && (
          <div className="px-4 py-1 text-[10px] text-red-300 border-t border-slate-800 shrink-0">{error}</div>
        )}

        {/* Caption — bottom, only when present */}
        {photo.caption && (
          <div className="px-4 py-2 border-t border-slate-800 text-[11px] text-slate-300 leading-snug shrink-0">
            {photo.caption}
          </div>
        )}
      </div>
    </div>
  );
}
